import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useRef, useState } from "react";
import { CheckCircle2, ImageUp, Loader2, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Panel, PrimaryButtonClass } from "@/components/common/Chrome";
import { TenantMark } from "@/components/common/TenantMark";
import { api, apiError } from "@/lib/api";
import { cn } from "@/lib/utils";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import { dateTime } from "@/lib/format";

const BACKEND = BACKEND_URL;

const SYSTEM_BANNERS = [
  { key: "hope", label: "Hope today, change tomorrow", src: `${process.env.PUBLIC_URL || ""}/backdrops/hope.jpg` },
  { key: "human-values", label: "Upholding human values", src: `${process.env.PUBLIC_URL || ""}/backdrops/human-values.jpg` },
  { key: "helping-hands", label: "Helping hands", src: `${process.env.PUBLIC_URL || ""}/backdrops/helping-hands.jpg` },
  { key: "himalayas", label: "Sun-kissed Himalayas", src: `${process.env.PUBLIC_URL || ""}/backdrops/himalayas.jpg` },
  { key: "waterfall", label: "Nature's waterfall", src: `${process.env.PUBLIC_URL || ""}/backdrops/waterfall.jpg` },
];

const PRESETS = [
  { hex: "#1E3A8A", label: "Deep corporate indigo" },
  { hex: "#10B981", label: "Soft mint emerald" },
  { hex: "#5B4FE0", label: "ServeCOR violet" },
  { hex: "#0F766E", label: "Charity teal" },
  { hex: "#B45309", label: "Warm amber" },
  { hex: "#BE123C", label: "Appeal crimson" },
  { hex: "#334155", label: "Slate" },
];

export const BrandingSettings = () => {
  const { can } = useAuth();
  const { refreshBackdrop, setAccent } = useTheme();
  const fileRef = useRef(null);
  const logoRef = useRef(null);
  const [state, setState] = useState(null);
  const [organisation, setOrganisation] = useState("");
  const [monogram, setMonogram] = useState("");
  const [busy, setBusy] = useState("");
  const [scrimDraft, setScrimDraft] = useState(null);
  const scrimTimer = useRef(null);

  const load = useCallback(async () => {
    const { data } = await api.get("/admin/branding");
    setState(data);
    const tenant = await api.get("/admin/branding/tenant").catch(() => null);
    if (tenant) {
      setOrganisation(tenant.data.organisation || "");
      setMonogram(tenant.data.monogram || "");
    }
  }, []);

  useEffect(() => { load().catch(() => setState(false)); }, [load]);

  if (!state) return null;
  const editable = can("organization", "write");

  const upload = async (file) => {
    if (!file) return;
    setBusy("upload");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/branding/backdrop", form,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      setState(data);
      await refreshBackdrop();
      toast.success("Backdrop updated — it is live for everyone");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const reset = async () => {
    setBusy("reset");
    try {
      const { data } = await api.delete("/admin/branding/backdrop");
      setState(data);
      await refreshBackdrop();
      toast.success("Reverted to the selected system banner");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const chooseBanner = async (key) => {
    setBusy(`banner-${key}`);
    try {
      const { data } = await api.put("/admin/branding/backdrop-choice", { choice: key });
      setState(data);
      await refreshBackdrop();
      toast.success("Backdrop updated — it is live for everyone");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const activeBanner = state.backdrop_choice || "hope";
  const scrim = scrimDraft ?? state.backdrop_scrim ?? 80;

  const previewScrim = (value) => {
    setScrimDraft(value);
    document.documentElement.style.setProperty("--scrim", String(value / 100));
  };

  const saveScrim = (value) => {
    // Debounced: rapid keyboard steps fire one PUT with the final value only.
    clearTimeout(scrimTimer.current);
    scrimTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.put("/admin/branding/backdrop-scrim", { scrim: value });
        setState(data);
        setScrimDraft(null);
        await refreshBackdrop();
        toast.success("Overlay strength saved — it is live for everyone");
      } catch (e) {
        toast.error(apiError(e));
        setScrimDraft(null);
        await refreshBackdrop();
      }
    }, 500);
  };

  const previewUrl = state.has_custom_backdrop
    ? `${BACKEND}${state.backdrop_url}`
    : (SYSTEM_BANNERS.find((b) => b.key === activeBanner) || SYSTEM_BANNERS[0]).src;

  const uploadLogo = async (file) => {
    if (!file) return;
    setBusy("logo");
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/branding/logo", form,
                                      { headers: { "Content-Type": "multipart/form-data" } });
      setState(data);
      toast.success("Logo saved — it is on the sign-in screen and every PDF now");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const removeLogo = async () => {
    setBusy("logo-remove");
    try {
      const { data } = await api.delete("/admin/branding/logo");
      setState(data);
      toast.success("Logo removed — the monogram is back");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  const saveAccent = async (hex) => {
    setBusy("accent");
    try {
      const { data } = await api.put("/admin/branding/accent", { accent: hex });
      setState(data);
      setAccent(data.accent || "");
      toast.success(hex ? "Accent colour saved — the whole console follows it"
                        : "Back to the platform accent");
    } catch (e) {
      toast.error(apiError(e));
    }
    setBusy("");
  };

  return (
    <div className="space-y-4">
    <Panel testId="panel-accent" kicker="Your brand" title="Accent colour">
      <div className="space-y-4 p-4">
        <p className="text-[12.5px] text-muted-foreground">
          One colour drives the console: buttons, links, focus rings and the first series on every
          chart. Pick one of your brand colours, or type the hex code your designer gave you.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          {PRESETS.map((p) => (
            <button key={p.hex} type="button" title={p.label}
                    data-testid={`accent-preset-${p.hex.slice(1).toLowerCase()}`}
                    onClick={() => editable && saveAccent(p.hex)}
                    className={cn("h-9 w-9 rounded-full ring-2 ring-offset-2 ring-offset-background transition-transform duration-150 hover:scale-110",
                                  (state.accent || "").toUpperCase() === p.hex
                                    ? "ring-foreground" : "ring-transparent")}
                    style={{ background: p.hex }} />
          ))}
          <input type="color" data-testid="accent-picker" disabled={!editable}
                 value={state.accent || "#5B4FE0"}
                 onChange={(e) => saveAccent(e.target.value.toUpperCase())}
                 className="h-9 w-12 cursor-pointer rounded-lg border border-border bg-transparent p-0.5" />
          <span className="font-mono text-[11px] text-muted-foreground" data-testid="accent-value">
            {state.accent || "platform default"}
          </span>
          {editable && state.accent && (
            <Button variant="outline" onClick={() => saveAccent("")} disabled={busy === "accent"}
                    data-testid="accent-reset-button" className="rounded-full">
              {busy === "accent" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                : <RotateCcw className="mr-1.5 h-4 w-4" />} Platform default
            </Button>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <Button className={PrimaryButtonClass} data-testid="accent-sample-button">
            This is your accent
          </Button>
          <span className="text-[12px] text-primary" data-testid="accent-sample-text">
            and so are links like this one
          </span>
        </div>
        {!editable && (
          <p className="text-[11px] text-muted-foreground">
            Only a tenant administrator can change the accent colour.
          </p>
        )}
      </div>
    </Panel>

    <Panel testId="panel-tenant-logo" kicker="Your brand" title="Charity logo">
      <div className="space-y-4 p-4">
        <p className="text-[12.5px] text-muted-foreground">
          Upload your own logo and it replaces ours on the sign-in screen and on the front of every
          PDF this system issues — certificates, minute sheets, the handbook. With nothing uploaded,
          the sign-in screen shows a monogram made from your charity's name.
        </p>

        <div className="flex flex-wrap items-center gap-4">
          <TenantMark
            logoUrl={state.has_custom_logo ? `${BACKEND}${state.logo_url}` : ""}
            name={organisation} monogram={monogram} size={84} testId="logo-preview" />
          <div className="space-y-2">
            <input ref={logoRef} type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden"
                   data-testid="logo-file-input"
                   onChange={(e) => uploadLogo(e.target.files?.[0])} />
            <div className="flex flex-wrap items-center gap-2">
              {editable && (
                <>
                  <Button onClick={() => logoRef.current?.click()} disabled={busy === "logo"}
                          data-testid="logo-upload-button" className={PrimaryButtonClass}>
                    {busy === "logo" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      : <ImageUp className="mr-1.5 h-4 w-4" />}
                    {state.has_custom_logo ? "Replace logo" : "Upload logo"}
                  </Button>
                  {state.has_custom_logo && (
                    <Button variant="outline" onClick={removeLogo} disabled={busy === "logo-remove"}
                            data-testid="logo-remove-button" className="rounded-full">
                      {busy === "logo-remove" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        : <Trash2 className="mr-1.5 h-4 w-4" />} Remove
                    </Button>
                  )}
                </>
              )}
              <span className="text-[11px] text-muted-foreground">PNG, JPG or WebP · up to 2 MB</span>
            </div>
            {state.has_custom_logo && (
              <p className="text-[11px] text-muted-foreground" data-testid="logo-meta">
                {state.logo_filename} · {Math.max(1, Math.round((state.logo_size || 0) / 1024))} KB · uploaded by{" "}
                {state.logo_uploaded_by} · {dateTime(state.logo_uploaded_at)}
              </p>
            )}
            {!state.has_custom_logo && (
              <p className="text-[11px] text-muted-foreground" data-testid="logo-monogram-note">
                Showing the monogram for {organisation || "your charity"}.
              </p>
            )}
          </div>
        </div>
        {!editable && (
          <p className="text-[11px] text-muted-foreground">
            Only a tenant administrator can change the charity logo.
          </p>
        )}
      </div>
    </Panel>

    <Panel testId="panel-branding" kicker="Appearance" title="Console backdrop">
      <div className="space-y-4 p-4">
        <p className="text-[12.5px] text-muted-foreground">
          The console layers frosted-glass panels over a photograph — bold in dark mode, soft in
          light mode. Pick one of the system banners below, or upload your own image — a wide,
          low-contrast landscape works best. Every user in this tenant sees it.
        </p>

        <div className="grid grid-cols-2 gap-2.5 sm:grid-cols-3 lg:grid-cols-5"
             data-testid="backdrop-banner-gallery">
          {SYSTEM_BANNERS.map((b) => {
            const selected = !state.has_custom_backdrop && activeBanner === b.key;
            return (
              <button key={b.key} type="button" disabled={!editable || busy !== ""}
                      onClick={() => chooseBanner(b.key)}
                      data-testid={`backdrop-preset-${b.key}`}
                      className={cn(
                        "group relative overflow-hidden rounded-xl border text-left transition-all duration-200",
                        selected ? "border-primary ring-2 ring-primary/50"
                                 : "border-border hover:border-primary/50",
                        !editable && "cursor-not-allowed opacity-80",
                      )}>
                <img src={b.src} alt={b.label} loading="lazy"
                     className="h-20 w-full object-cover transition-transform duration-300 group-hover:scale-105" />
                {selected && (
                  <span className="absolute right-1.5 top-1.5 rounded-full bg-primary p-0.5 text-primary-foreground"
                        data-testid={`backdrop-selected-${b.key}`}>
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </span>
                )}
                {busy === `banner-${b.key}` && (
                  <span className="absolute inset-0 grid place-items-center bg-black/40">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                  </span>
                )}
                <span className="block truncate px-2 py-1 text-[10.5px] text-muted-foreground">
                  {b.label}
                </span>
              </button>
            );
          })}
        </div>

        <div className="relative overflow-hidden rounded-xl border border-border" data-testid="backdrop-preview">
          <img src={previewUrl} alt="Dark mode backdrop preview"
               className="h-44 w-full object-cover" />
          <div className="pointer-events-none absolute inset-0" data-testid="backdrop-scrim-overlay"
               style={{ background: `linear-gradient(180deg, hsl(217 65% 11% / ${(scrim / 100) * 0.65}) 0%, hsl(221 60% 8% / ${scrim / 100}) 100%)` }} />
        </div>

        <div className="space-y-1.5" data-testid="backdrop-scrim-control">
          <div className="flex items-center justify-between">
            <span className="text-[12px] font-medium text-foreground">Overlay strength</span>
            <span className="font-mono text-[11.5px] text-muted-foreground" data-testid="backdrop-scrim-value">
              {scrim}%
            </span>
          </div>
          <Slider value={[scrim]} min={20} max={100} step={5} disabled={!editable}
                  data-testid="backdrop-scrim-slider"
                  className={cn(!editable && "opacity-45")}
                  onValueChange={(v) => previewScrim(v[0])}
                  onValueCommit={(v) => saveScrim(v[0])} />
          <p className="text-[11px] text-muted-foreground">
            Deepen the overlay so any lettering in the photograph never competes with figures on
            screen — 20% keeps the image airy, 100% is close to a plain surface. The preview above
            follows the slider live.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden"
                 data-testid="backdrop-file-input"
                 onChange={(e) => upload(e.target.files?.[0])} />
          {editable && (
            <>
              <Button onClick={() => fileRef.current?.click()} disabled={busy === "upload"}
                      data-testid="backdrop-upload-button" className={PrimaryButtonClass}>
                {busy === "upload" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ImageUp className="mr-1.5 h-4 w-4" />}
                Upload image
              </Button>
              {state.has_custom_backdrop && (
                <Button variant="outline" onClick={reset} disabled={busy === "reset"}
                        data-testid="backdrop-reset-button" className="rounded-full">
                  {busy === "reset" ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-1.5 h-4 w-4" />}
                  Back to system banners
                </Button>
              )}
            </>
          )}
          <span className="text-[11px] text-muted-foreground">JPG, PNG or WebP · up to 8 MB</span>
        </div>

        {state.has_custom_backdrop && (
          <p className="flex items-center gap-1.5 text-[11px] text-muted-foreground" data-testid="backdrop-meta">
            <Trash2 className="hidden h-3 w-3" />
            {state.original_filename} · {Math.round((state.size || 0) / 1024)} KB · uploaded by{" "}
            {state.uploaded_by} · {dateTime(state.uploaded_at)}
          </p>
        )}
        {!editable && (
          <p className="text-[11px] text-muted-foreground">
            Only a tenant administrator can change the console backdrop.
          </p>
        )}
      </div>
    </Panel>
    </div>
  );
};
